/**
 * Who sold this order or quotation, as a display string.
 *
 * There are TWO sources on the row and they are not the same fact, so this prefers one
 * and falls back to the other rather than picking:
 *
 *   1. `salesOwner` -- `{ id, name }` resolved server-side from the `salesOwnerId`
 *      column, which is written forward from the LSQ opportunity owner. A real PillUp
 *      identity. This is the one the Sales Console actually scopes on.
 *
 *   2. The genesis status transition's `actor.name` -- whoever LSQ says first touched the
 *      entity. A display string with no identity behind it; it was the only source before
 *      salesOwnerId existed.
 *
 * WHY THE FALLBACK IS NOT OPTIONAL
 *
 * `salesOwnerId` was added with NO BACKFILL, so every order and quotation created before
 * it shipped has `salesOwner: null` -- in practice all ~13k existing orders. Reading only
 * the new field would blank a column that shows a name today, which reads as a broken
 * page rather than as a migration in progress. The fallback means the column can only get
 * better as attribution fills in.
 *
 * The genesis subtlety is load-bearing and is why this is shared rather than restated:
 * `statusHistory[0]` is frequently an unrelated REQUEST_CALLBACK webhook row, so matching
 * on `refEntityType` AND `fromStatus === null` is what picks the entity's own creation
 * rather than something adjacent to it.
 */

/**
 * @param {object} original the react-table row's `original` object
 * @param {"USER_ORDER"|"QUOTATION"} entityType which entity's genesis transition to read
 * @returns {string|null} a name, or null to render as empty
 */
export function salesPersonName(original, entityType = "USER_ORDER") {
    if (!original) return null;

    // The attributed owner wins whenever we have a name for them. `salesOwner` can be
    // present with a null name -- owned, but MyTatva could not name them -- in which case
    // the older actorName is still better than showing nothing.
    const ownerName = original.salesOwner?.name;
    if (ownerName) return ownerName;

    const history = original.statusHistory || [];
    const genesis = history.find((t) => t.refEntityType === entityType && t.fromStatus === null);
    return genesis?.actor?.name || null;
}

export default salesPersonName;
